import { cva, type VariantProps } from 'class-variance-authority';
import { createContext, ReactNode, use } from 'react';

// `medium`/`large` are opt-in per screen rather than the shared default so
// they don't reach every ScrollLayout consumer at once.
const innerLayout = cva('w-full md:max-w-512 mx-auto flex-col space-y-m', {
  variants: {
    padding: {
      default: 'p-xl',
      medium: 'p-m',
      large: 'p-xxl',
    },
  },
  defaultVariants: { padding: 'default' },
});
const footerInnerLayout = cva(
  'w-full md:max-w-512 mx-auto flex-col space-y-m',
  {
    variants: {
      padding: {
        default: 'p-m',
        medium: 'p-m',
        large: 'p-xxl',
      },
    },
    defaultVariants: { padding: 'default' },
  },
);

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

const scrollLayout = cva('w-full h-full flex flex-col');
type ScrollLayoutElementProps = { children: ReactNode };
type ScrollLayoutPadding = VariantProps<typeof innerLayout>['padding'];

const ScrollLayoutPaddingContext =
  createContext<ScrollLayoutPadding>('default');

const ScrollLayoutRoot = ({
  children,
  padding = 'default',
}: ScrollLayoutElementProps & { padding?: ScrollLayoutPadding }) => {
  return (
    <ScrollLayoutPaddingContext value={padding}>
      <div className={scrollLayout()}>{children}</div>
    </ScrollLayoutPaddingContext>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Header
 * -----------------------------------------------------------------------------------------------*/

const scrollLayoutHeader = cva('sticky top-0 shrink-0');

export const ScrollLayoutHeader = ({ children }: ScrollLayoutElementProps) => {
  return <div className={scrollLayoutHeader()}>{children}</div>;
};

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const scrollLayoutContent = cva('flex-1 overflow-y-auto');

const ScrollLayoutContent = ({ children }: ScrollLayoutElementProps) => {
  const padding = use(ScrollLayoutPaddingContext);
  return (
    <div className={scrollLayoutContent()}>
      <div className={innerLayout({ padding })}>{children}</div>
    </div>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Footer
 * -----------------------------------------------------------------------------------------------*/

const scrollLayoutFooter = cva('bg-surface border-t shrink-0');

const ScrollLayoutFooter = ({ children }: ScrollLayoutElementProps) => {
  const padding = use(ScrollLayoutPaddingContext);
  return (
    <footer className={scrollLayoutFooter()}>
      <div className={footerInnerLayout({ padding })}>{children}</div>
    </footer>
  );
};

export {
  ScrollLayoutContent as Content,
  ScrollLayoutFooter as Footer,
  ScrollLayoutHeader as Header,
  ScrollLayoutRoot as Root,
};
